// docMigration.ts — detect and flag stale blob: URL entries across user-uploaded
// document arrays. Renamed from profileDocMigration.ts (Day 50, B23) when the
// same migration shape was needed for StructureDocument[] and PropertyDocument[].
// See LESSONS.md L21: blob URLs are session-scoped and die on refresh.

import type { VerificationDocument, TaxDocument, PayslipEntry, StructureDocument } from "../types/entities";
import type { PropertyDocument } from "../types/portfolio";

/** Returns true if a doc has a filename but no valid Supabase Storage path */
function isStaleDoc(doc: { filename?: string; url?: string; filePath?: string; needsReupload?: boolean }): boolean {
  if (doc.needsReupload) return true;
  if (!doc.filename) return false;
  if (doc.filePath) return false; // has a real storage path
  // Has filename but no filePath — either legacy blob: URL or missing
  return true;
}

/** Migrate a single doc: clear stale url, set needsReupload flag */
function migrateDoc<T extends { url?: string; filePath?: string; needsReupload?: boolean; filename?: string }>(doc: T): T {
  if (!isStaleDoc(doc)) return doc;
  return { ...doc, url: undefined, needsReupload: true };
}

/** Migrate all profile document arrays. Returns updated arrays + whether any changes were made. */
export function migrateProfileDocs(
  verDocs: VerificationDocument[],
  taxDocs: TaxDocument[],
  payslips: PayslipEntry[],
): {
  verificationDocuments: VerificationDocument[];
  taxDocuments: TaxDocument[];
  payslips: PayslipEntry[];
  changed: boolean;
} {
  const migratedVer = verDocs.map(migrateDoc);
  const migratedTax = taxDocs.map(migrateDoc);
  const migratedPay = payslips.map(migrateDoc);

  const changed =
    migratedVer.some((d, i) => d.needsReupload !== verDocs[i].needsReupload) ||
    migratedTax.some((d, i) => d.needsReupload !== taxDocs[i].needsReupload) ||
    migratedPay.some((d, i) => d.needsReupload !== payslips[i].needsReupload);

  return {
    verificationDocuments: migratedVer,
    taxDocuments: migratedTax,
    payslips: migratedPay,
    changed,
  };
}

export { isStaleDoc };

/** Migrate a structure document array. Returns updated array + whether any changes were made. */
export function migrateStructureDocs(docs: StructureDocument[]): {
  documents: StructureDocument[];
  changed: boolean;
} {
  const migrated = docs.map(migrateDoc);
  const changed = migrated.some((d, i) => d.needsReupload !== docs[i].needsReupload);
  return { documents: migrated, changed };
}

/** Migrate a property document array. Returns updated array + whether any changes were made. */
export function migratePropertyDocs(docs: PropertyDocument[]): {
  documents: PropertyDocument[];
  changed: boolean;
} {
  const migrated = docs.map(migrateDoc);
  const changed = migrated.some((d, i) => d.needsReupload !== docs[i].needsReupload);
  return { documents: migrated, changed };
}
